import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import type { Customer, CustomerPhoneType } from "@autorepair/contracts";
import { ApiError, customersApi } from "../lib/api";
import { formatPhoneAsTyped } from "../lib/phone";
import { applyDigitPreservingTransform } from "../lib/inputCursor";
import { useConfirmGate } from "../lib/useConfirm";
import { Spinner } from "../components/ui-modern";
import {
  ModernBreadcrumbs,
  ModernButton,
  ModernCard,
  ModernConfirmDialog,
  ModernErrorBanner,
  ModernField,
  ModernInput,
  ModernSelect,
} from "../components/ui-modern";

function initialValues(customer: Customer) {
  const primaryPhone = customer.phones.find((p) => p.isPrimary) ?? customer.phones[0];
  return {
    displayName: customer.displayName,
    email: customer.email ?? "",
    // Defaults to Mobile, not Home — matches CustomerForm.tsx's own default
    // for a customer with no phone on file yet.
    phoneType: (primaryPhone?.phoneType ?? "mobile") as CustomerPhoneType,
    phoneNumber: primaryPhone?.phoneNumber ?? "",
  };
}

/**
 * The modern theme's Edit Customer page — see docs/FRONTEND_BACKLOG.md
 * Round 62 and NewCustomerPageModern.tsx's own header comment for why this
 * inlines CustomerForm.tsx's field set/logic rather than reusing that
 * (out-of-scope, classic-styled) shared component. Same
 * queryKey/queryFn/setQueryData as EditCustomerPageClassic.tsx — same cache
 * entry, no shared hook.
 */
export function EditCustomerPageModern() {
  const { customerNumber: customerNumberParam } = useParams();
  const customerNumber = Number(customerNumberParam);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error: loadError } = useQuery({
    queryKey: ["customer", "by-number", customerNumber],
    queryFn: () => customersApi.getByNumber(customerNumber),
  });
  const id = data?.customer.id as number;

  const [values, setValues] = useState<ReturnType<typeof initialValues> | null>(null);
  const current = values ?? (data ? initialValues(data.customer) : null);

  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const confirmGate = useConfirmGate();

  function set<K extends keyof ReturnType<typeof initialValues>>(key: K, value: ReturnType<typeof initialValues>[K]): void {
    setValues((v) => ({ ...(v ?? initialValues(data!.customer)), [key]: value }));
  }

  async function save(): Promise<void> {
    if (!current) return;
    setError(null);
    setSubmitting(true);
    try {
      const result = await customersApi.update(id, {
        displayName: current.displayName,
        email: current.email.trim() || null,
        // Address fields deliberately not sent — see
        // NewCustomerPageModern.tsx's own doc comment. Omitting them from
        // this PATCH (rather than sending null) leaves whatever a
        // customer already had on file untouched, same as any other field
        // this form doesn't offer.
        phones: current.phoneNumber.trim()
          ? [{ phoneType: current.phoneType, phoneNumber: current.phoneNumber.trim(), isPrimary: true, sortOrder: 0 }]
          : [],
      });
      queryClient.setQueryData(["customer", "by-number", customerNumber], result);
      navigate(`/customers/${customerNumber}`);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Could not save this customer. Please try again.");
    } finally {
      setSubmitting(false);
    }
  }

  if (isLoading) return <Spinner label="Loading customer…" />;
  if (loadError || !data || !current) return <ModernErrorBanner message="Could not load this customer." />;

  return (
    <div className="mx-auto max-w-xl">
      <ModernBreadcrumbs
        items={[
          { label: "Customers", to: "/customers" },
          { label: data.customer.displayName, to: `/customers/${customerNumber}` },
          { label: "Edit" },
        ]}
      />
      <h1 className="m-0 mb-4 text-[27px] font-bold text-[#071a33]">Edit Customer</h1>

      <ModernCard>
        {error && <ModernErrorBanner message={error} />}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            confirmGate.request(() => void save());
          }}
        >
          <ModernField label="Customer's Name" htmlFor="displayName" required>
            <ModernInput
              id="displayName"
              required
              autoFocus
              uppercase
              value={current.displayName}
              onChange={(e) => set("displayName", e.target.value)}
            />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">How they&apos;d like to be addressed, e.g. their full name.</p>
          </ModernField>

          <ModernField label="Phone Type" htmlFor="phoneType">
            <ModernSelect id="phoneType" value={current.phoneType} onChange={(e) => set("phoneType", e.target.value as CustomerPhoneType)}>
              <option value="mobile">Mobile</option>
              <option value="home">Home</option>
              <option value="work">Work</option>
            </ModernSelect>
          </ModernField>
          <ModernField label="Phone Number" htmlFor="phoneNumber">
            <ModernInput
              id="phoneNumber"
              type="tel"
              value={current.phoneNumber}
              onChange={(e) => set("phoneNumber", applyDigitPreservingTransform(e.target, formatPhoneAsTyped))}
            />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">Optional, but makes them much easier to find later.</p>
          </ModernField>

          <ModernField label="Email" htmlFor="email">
            <ModernInput id="email" type="email" value={current.email} onChange={(e) => set("email", e.target.value)} />
            <p className="mt-1.5 text-[12.5px] text-[#8b97a8]">
              Optional. To email estimates and invoices to this customer directly from Bay2Bill, enter a valid email address they check.
            </p>
          </ModernField>

          <div className="mt-5 flex gap-2.5">
            <ModernButton type="submit" disabled={submitting}>
              {submitting ? "Saving…" : "Save Customer"}
            </ModernButton>
            <ModernButton type="button" variant="secondary" onClick={() => navigate(`/customers/${customerNumber}`)}>
              Cancel
            </ModernButton>
          </div>
        </form>
      </ModernCard>

      <ModernConfirmDialog {...confirmGate.dialogProps} message="Save these changes to the customer?" />
    </div>
  );
}
